import { Component, OnDestroy, OnInit } from "@angular/core";
import { map } from "rxjs/operators";
import { Breakpoints, BreakpointObserver } from "@angular/cdk/layout";
import {
  TopSenders,
  OpensAndViews,
  RecentComment,
  RecentCta,
  RecentReactions,
} from "./dashboard.model";
import { DashboardService } from "./dashboard.service";
import { CorpClient } from "../shared-models/client.model";
import { CorpClientsService } from "../corp-clients.service";
import { Auth } from "aws-amplify";
import { CognitoUser } from "@aws-amplify/auth";
import { Observable, Subscription } from "rxjs";
import { Router } from "@angular/router";
import { MySalesMailResult } from "../my-results/my-results.service";
import { PortalSettingsService } from "../portal-settings.service";
import { AppBanner, AppNotification, NotificationType } from "../shared-models/settings.model";
import { AnalyticsEventName, AppAnalyticsService } from "src/app-analytics.service";
import { MatTabChangeEvent } from "@angular/material/tabs";
import { UsersService } from "../users/users.service";

export type DashboardLeaderboardMenuItem = 'views' | 'tes' | 'videos';

@Component({
    selector: "app-dashboard",
    templateUrl: "./dashboard.component.html",
    styleUrls: ["./dashboard.component.css"],
    standalone: false
})
export class DashboardComponent implements OnInit, OnDestroy {
  private readonly opensTitle = "Engagements";
  private readonly usersTitle = "Top Senders";
  private readonly recentsTitle = "Recents";
  private teamResults = "Team Results";
  private readonly stats = "Money Making Hours";
  private readonly usageTitle = "New Videos Per User";
  private readonly rankTitle = "SalesMail Rank";
  private readonly leaderboardTitle = "Leaderboard";
  private leaderboardType: DashboardLeaderboardMenuItem = 'tes';

  private topSenderData: TopSenders[];
  private topSenderError: String;
  private topSenderDays: number = 90;
  private topSendersLoading: boolean = false;

  private opensAndViewsData: OpensAndViews[];
  private opensAndViewsError: String;
  private opensAndViewsDays: number = 90;
  private opensAndViewsLoading: boolean = false;

  private statsDays: number = 90;
  private opensAndViewsStatsData: OpensAndViews[];
  private statsError: string;
  private statsLoading: boolean = false;

  private teamResultsDays: number = 90;
  private usageDays: number = 90;

  recent_comments: RecentComment[] = [];
  recent_cta: RecentCta[] = [];
  recent_reactions: RecentReactions[] = [];

  recent_comments_empty: boolean = false;
  recent_cta_empty: boolean = false;
  recent_reactions_empty: boolean = false;

  currentClient: CorpClient;
  cognitoUser: CognitoUser;
  isAdmin: boolean;

  showAchievements: boolean = false;

  cards: Observable<{ title: string; cols: number; rows: number }[]>;

  banner: AppBanner = null;
  notifications: AppNotification[] = [];
  public subscriptions: Subscription[] = [];

  constructor(
    private breakpointObserver: BreakpointObserver,
    private api: DashboardService,
    private corpService: CorpClientsService,
    private router: Router,
    private portalSettingsService: PortalSettingsService,
    private analyticsService: AppAnalyticsService,
    private userService: UsersService
  ) {
      this.doSetup();
  }

  ngOnInit() {
    this.checkAchievements();
    this.portalSettingsService.loadData(); //Refresh data
    this.portalSettingsService.ping();
    this.analyticsService.log('dashboard_view');
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  async doSetup() {
    await this.checkAchievements();
    this.corpService.currentClient.subscribe(async (result) => {
      
      this.cognitoUser = await Auth.currentAuthenticatedUser();
      let payload = this.cognitoUser
        .getSignInUserSession()
        .getAccessToken()
        .decodePayload();

      this.isAdmin = await this.userService.isAuthenticatedUserClientAdmin(); 
      
      if (this.isAdmin) {      
        if (result != null) {
          this.teamResults = "Team Results";

          this.setupCardObservable(false);
          await this.checkAchievements();
          this.currentClient = result;
          this.getTopSenderData();
          this.getOpenAndViewsDataForAll();
        }
      } else {   
          this.teamResults = "My Results";
          this.setupCardObservable(true);  
          await this.checkAchievements();
          this.getOpenAndViewsDataForUser();         
      }

      try {
        this.getRecents();
      } catch (err) {          
        console.error(err);
      }
    });

    const bannerSub =  this.portalSettingsService.ActiveBanner.subscribe((result => {
      this.banner = result;
    }));

    const notificationSub = this.portalSettingsService.ActiveNotifications.subscribe(result => {
      this.notifications = result;
    });

    this.subscriptions.push(bannerSub, notificationSub);
  }

  async checkAchievements() {    
    this.showAchievements = await this.api.shouldEnableGamification();    
  }

  async setupCardObservable(stdUser: boolean) {
    
    this.cards = this.breakpointObserver.observe(Breakpoints.Handset).pipe(
      map(({ matches }) => {        
        if (stdUser) { // user is a standard user
          if (matches) { // Is a handset sized display
            return [
              { title: this.opensTitle, cols: 2, rows: 1 },
              ...(!this.showAchievements ? [] : [
                { title: this.leaderboardTitle, cols: 2, rows: 2 },
                { title: this.rankTitle, cols: 2, rows: 1 },
              ]),
              
              { title: this.recentsTitle, cols: 2, rows: 1 },    
              { title: this.teamResults, cols: 2, rows: 1 },                                      
            ];
          } else { // Standard user and not a handset
            return [
              { title: this.opensTitle, cols: 2, rows: 1 },
              ...(!this.showAchievements ? [
                { title: this.recentsTitle, cols: 1, rows: 2 },              
                { title: this.teamResults, cols: 1, rows: 1 },              
              ] : [
                { title: this.leaderboardTitle, cols: 1, rows: 2 },
                { title: this.rankTitle, cols: 1, rows: 1 },
                { title: this.recentsTitle, cols: 1, rows: 2 },              
                { title: this.teamResults, cols: 1, rows: 1 },              
              ]),              
            ];
          }
        }

        if (matches) { // corp or admin user, and is a handset
          return [
            { title: this.opensTitle, cols: 2, rows: 1 },
            ...(!this.showAchievements ? [] : [
              { title: this.leaderboardTitle, cols: 2, rows: 2 },
              { title: this.rankTitle, cols: 2, rows: 1 },
            ]),
            { title: this.recentsTitle, cols: 2, rows: 1 },
            { title: this.teamResults, cols: 2, rows: 1 },
            { title: this.usersTitle, cols: 2, rows: 1 },
            { title: this.usageTitle, cols: 2, rows: 1 },
          ];
        } else { // corp or admin user, and not a handset
          return [
            { title: this.opensTitle, cols: 2, rows: 1 },            
            ...(!this.showAchievements ? [
              { title: this.usersTitle, cols: 1, rows: 2 },
              { title: this.recentsTitle, cols: 1, rows: 1 },
              { title: this.teamResults, cols: 1, rows: 1 },
            ] : [
              { title: this.leaderboardTitle, cols: 1, rows: 2 },
              { title: this.rankTitle, cols: 1, rows: 1 },
              { title: this.teamResults, cols: 1, rows: 1 },
              { title: this.usersTitle, cols: 1, rows: 2 },
              { title: this.recentsTitle, cols: 1, rows: 2 }, 
            ]),
            { title: this.usageTitle, cols: 2, rows: 1 },
          ];
        }
      })
    );    
  }

  getClassStringForNotificationType(type: NotificationType) {
    return this.portalSettingsService.getClassStringForNotificationType(type);
  }

  async getOpenAndViewsDataForUser(days: number = 30, bMoneyMaking = true, bEngagements = true ) {
    
    if (bEngagements) {
      this.opensAndViewsLoading = true;
      this.opensAndViewsDays = days;
      this.opensAndViewsError = null;      
    }

    if (bMoneyMaking) {
      this.statsLoading = true;
      this.statsDays = days;
      this.statsError = null;      
    }

    let comments = await this.api.getStdComments(days);
    let reactions = await this.api.getStdReactions(days);
    let ctas = await this.api.getStdCtas(days);
    let salesmails = await this.api.getStdSalesmails(days);

    this.api
      .getOpensAndViewsStd(days)
      .then((response) => {
        let merger = [
          ...ctas.value,
          ...reactions.value,
          ...comments.value,
          ...salesmails.value,
          ...response.value,
        ];

        if (bEngagements) {
          this.opensAndViewsData = merger || [];
        }

        if (bMoneyMaking) {
          this.opensAndViewsStatsData = merger;
        }
      })
      .catch((error) => {
        console.log(error);
        this.opensAndViewsError = "Could not load chart data at this time.";
        this.statsError = "Could not load chart data at this time.";
      })
      .finally(() => {
        this.opensAndViewsLoading = false;
        this.statsLoading = false;
      });
  }

  async onRecentTabChange(event: MatTabChangeEvent) {
    const tabs: AnalyticsEventName[] = ['dashboard_recent_comments', 'dashboard_recent_cta', 'dashboard_recent_reactions'];
    this.analyticsService.log(tabs[event.index]);
  }

  topSendersChange(days: number) {
    this.getTopSenderData(days);
    this.analyticsService.log('dashboard_top_senders', days);
  }

  async getTopSenderData(days: number = 90) {
    this.topSendersLoading = true;
    this.topSenderError = null;    
    this.api
      .getTopSenders(days, this.currentClient.client_id)
      .then((response) => {
        this.topSenderDays = days;
        this.topSenderData = response.value;
      })
      .catch((error) => {
        console.log(error);
        this.topSenderError = "Could not load chart data at this time.";
      })
      .finally(() => {
        this.topSendersLoading = false;
      });
  }

  async getOpenAndViewsDataForAll(days: number = 30, bMoneyMaking = true, bEngagements = true ) {
    
    if (bEngagements) {
      this.opensAndViewsLoading = true;
      this.opensAndViewsDays = days;
      this.opensAndViewsError = null;
    }

    if (bMoneyMaking) {
      this.statsLoading = true;
      this.statsDays = days;
      this.statsError = null;
    }

    let comments = await this.api.getComments(
      days,
      this.currentClient.client_id
    );
    let reactions = await this.api.getReactions(
      days,
      this.currentClient.client_id
    );
    let ctas = await this.api.getCtas(days, this.currentClient.client_id);
    let salesmails = await this.api.getSalesmails(
      days,
      this.currentClient.client_id
    );

    this.api
      .getOpensAndViews(days, this.currentClient.client_id)
      .then((response) => {
        let merger = [
          ...ctas.value,
          ...reactions.value,
          ...comments.value,
          ...salesmails.value,
          ...response.value,
        ];

        if (bEngagements) {
          this.opensAndViewsData = merger || [];
        }

        if (bMoneyMaking) {
          this.opensAndViewsStatsData = merger;
        }
      })
      .catch((error) => {
        console.log(error);
        this.opensAndViewsError = "Could not load chart data at this time.";
        this.statsError = "Could not load chart data at this time.";
      })
      .finally(() => {
        this.opensAndViewsLoading = false;
        this.statsLoading = false;
      });
  }

  async getOpenAndViewsData(days: number = 90) {
    this.opensAndViewsLoading = true;
    this.opensAndViewsDays = days;
    this.opensAndViewsError = null;

    this.analyticsService.log('dashboard_engagements', days);

    let clientId = this.currentClient?.client_id ?? null;
    
    if (clientId == null || clientId == undefined) {
      this.getOpenAndViewsDataForUser(days, false, true);
    } else {
      this.getOpenAndViewsDataForAll(days, false, true);
    }
  }

  async getOpenAndViewsDataForStats(days: number = 90) {
    this.statsLoading = true;
    this.statsDays = days;
    this.statsError = null;
    let clientId = this?.currentClient?.client_id ?? null;

    if (clientId == null) {
      this.getOpenAndViewsDataForUser(days, true, false);
    } else {
      this.getOpenAndViewsDataForAll(days, true, false);
    }
    this.analyticsService.log('dashboard_mmh', days);
  }

  usageDaysChange(value: number) {
    this.usageDays = value;
    this.analyticsService.log('dashboard_user_usage', value);
  }

  teamDaysChange(value: number) {
    this.teamResultsDays = value;
    this.analyticsService.log('dashboard_team_results', value);
  }

  changeLeaderboardType(value: DashboardLeaderboardMenuItem) {
    this.leaderboardType = value;
    this.analyticsService.log('dashboard_leaderboard_type_change', value);
  }

  getButtonTextforLbType() {
    switch (this.leaderboardType) {
      case 'views':
        return "Top Views";

      case 'videos': 
        return "Top Videos";

      case 'tes':
        return 'Top Score';
    
      default:
        return "";
    }
  }

  getRecents() {
    if (!this.isAdmin) {
      this.api
        .getRecentComments<RecentComment>()
        .then((result) => {
          this.recent_comments = result.value;

          if (this.recent_comments.length == 0) {
            this.recent_comments_empty = true;
          }
        })
        .catch((error) => {
          console.debug(error);
        });

      this.api
        .getRecentCTAs<RecentCta>()
        .then((result) => {
          this.recent_cta = result.value;

          if (this.recent_cta.length == 0) {
            this.recent_cta_empty = true;
          }
        })
        .catch((error) => {
          console.debug(error);
        });

      this.api
        .getRecentReactions<RecentReactions>()
        .then((result) => {
          this.recent_reactions = result.value;

          if (this.recent_reactions.length == 0) {
            this.recent_reactions_empty = true;
          }
        })
        .catch((error) => {
          console.debug(error);
        });
    } else if(this.currentClient) {
      this.api
        .getRecentCommentsTeam<RecentComment>(this.currentClient.client_id)
        .then((result) => {
          this.recent_comments = result.value;

          if (this.recent_comments.length == 0) {
            this.recent_comments_empty = true;
          }
        })
        .catch((error) => {
          console.debug(error);
        });

      this.api
        .getRecentCTAsTeam<RecentCta>(this.currentClient.client_id)
        .then((result) => {
          this.recent_cta = result.value;

          if (this.recent_cta.length == 0) {
            this.recent_cta_empty = true;
          }
        })
        .catch((error) => {
          console.debug(error);
        });

      this.api
        .getRecentReactionsTeam<RecentReactions>(this.currentClient.client_id)
        .then((result) => {
          this.recent_reactions = result.value;

          if (this.recent_reactions.length == 0) {
            this.recent_reactions_empty = true;
          }
        })
        .catch((error) => {
          console.debug(error);
        });
    }
  }

  public viewDetails(row: MySalesMailResult, type: 'dashboard_comment_detail_click' | 'dashbord_cta_detail_click' | 'dashboard_reaction_detail_click') {
    this.analyticsService.log(type, row);
    row.id = row["video_id"];
    this.router.navigate(["my-videos", "detail"], {
      state: { salesmail: row, dashboard: true },
    });
  }

  onBannerClick() {
    if(!this.banner) {
      return;
    }
    this.analyticsService.log('banner_click', this.banner);
    this.portalSettingsService.recordBannerHit(this.banner.id).then(result => {      
    }).catch(error => {
      console.error("Error on banner click", error);
    })
  }

  didClickLbInfo() {
    this.analyticsService.log('dashboard_leaderboard_info_button');
  }

  didClickRankInfo() {
    this.analyticsService.log('dashboard_rank_info_button');
  }

}
